import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AddCameraWizardModels } from '../../models/wizard.models';
import { Camera } from '../../../../cameras/camera.model';
import { EdgeService } from '../../../../edge/edge.service';
import { SocketMainService } from '../../../../socket/socket-main.service';
import { SocketEvents } from '../../../../socket/socket.model';
import { map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { CameraTableType } from '../../../../pages/camera/components/camera-table/camera-table.component';
import * as uuid from 'uuid';
import { select, Store } from '@ngrx/store';
import { EdgeStatusSelectors } from '@states/edge-status/edge-status.selector-types';
import { Edge } from '../../../../edge/edge.model';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { RouteModalService } from '@ui';
import { CameraActivateDialogComponent, CameraActivateDialogData } from '../../../../pages/camera/dialogs/camera-activate-dialog/camera-activate-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { WizardCameraCsvUtil } from '../../utils/wizard-camera-csv.util';

@Component({
    selector: 'app-wizard-scan-selection',
    templateUrl: './wizard-scan-selection.component.html',
    styleUrls: ['./wizard-scan-selection.component.scss'],
    standalone: false,
})
export class WizardScanSelectionComponent implements OnInit, OnDestroy {
    @Input() edgeId: string;
    @Input() detectionMethod: AddCameraWizardModels.DetectionMethod;
    @Input() addressRangeData: AddCameraWizardModels.AddressRangeData | null;

    @Output() camerasSelected = new EventEmitter<Camera[]>();
    @Output() manualConfigChanged = new EventEmitter<{ deviceType: 'camera' | 'nvr'; ipArr: string[] }>();
    @Output() csvImportCompleted = new EventEmitter<AddCameraWizardModels.ManualCsvImportRow[]>();
    @Output() back = new EventEmitter<void>();

    private destroy$ = new Subject<void>();

    public isScanning: boolean = false;
    public scanProgress: number = 0;
    public discoveredCameras: Camera[] = [];
    public selectedCameras: Set<string> = new Set();

    public selectEdgeStatus$: Observable<Edge.V2.EdgePulsationDisplay>;

    // Session token to filter notifications from current scan only
    private currentSessionToken: string | null = null;

    // Filter types
    public filterTypes: CameraTableType[] = [CameraTableType.KNOWN_BRAND];
    public availableFilters = [
        { value: CameraTableType.KNOWN_BRAND, label: 'ADD_CAMERA_WIZARD.SCAN.FILTER.UNREGISTERED_CAMERAS' },
        { value: CameraTableType.UNKNOWN_BRAND, label: 'ADD_CAMERA_WIZARD.SCAN.FILTER.UNKNOWN_DEVICES' },
        { value: CameraTableType.ADDED, label: 'ADD_CAMERA_WIZARD.SCAN.FILTER.REGISTERED_CAMERAS' }
    ];

    public progressDone: boolean = false;
    public CameraTableType = CameraTableType;
    public AddCameraWizardModels = AddCameraWizardModels;

    public readonly searchControl = new FormControl<string>('', { nonNullable: true });
    public filteredCameras: Camera[] = [];

    // Manual selection form (shown only when detectionMethod === Manual)
    public manualForm: FormGroup;

    // CSV import state (shown only when detectionMethod === CsvImport)
    public csvFileName: string | null = null;
    public csvParseErrors: string[] = [];
    public csvImportedRows: AddCameraWizardModels.ManualCsvImportRow[] = [];

    // Camera naming counters (like original implementation)
    private unnamedCamerasCount = 1;
    private unnamedDevicesCount = 1;

    public displayedColumns: string[] = [
        'select',
        'name',
        'ipAddress',
        'mac',
        'minPing',
        'maxPing',
        'avgPing',
        'stdPing',
        'connection',
        'cameraBrand'
    ];

    private cameraNum = 1;
    private deviceNum = 1;

    constructor(
        private edgeService: EdgeService,
        private socketMainService: SocketMainService,
        private store$: Store,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private translateService: TranslateService
    ) { }

    ngOnInit(): void {
        if (this.detectionMethod === AddCameraWizardModels.DetectionMethod.Manual) {
            this.initManualForm();
        } else if (this.detectionMethod === AddCameraWizardModels.DetectionMethod.CsvImport) {
            this.manualConfigChanged.emit({ deviceType: 'camera', ipArr: [] });
        } else {
            this.startScan();
            this.subscribeToScanUpdates();
        }
        this.selectEdgeStatus$ = this.store$.pipe(select(EdgeStatusSelectors.selectEdgesStatusEntities)).pipe(
          map((entities) => entities[this.edgeId])
        );
        this.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.applySearch());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initManualForm(): void {
        this.manualForm = this.fb.group({
            deviceType: ['camera', Validators.required],
            ipArr: this.fb.array<FormControl<string>>([
                this.fb.control('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^(\d{1,3}\.){3}\d{1,3}$/)] })
            ])
        });

        this.manualForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            // NVR: only one IP is allowed
            const deviceType = (this.manualForm.get('deviceType')?.value || 'camera') as 'camera' | 'nvr';
            if (deviceType === 'nvr' && this.manualIps.length > 1) {
                // Keep only the first entry
                while (this.manualIps.length > 1) {
                    this.manualIps.removeAt(this.manualIps.length - 1);
                }
            }
            this.emitManualSelection();
        });

        this.emitManualSelection();
    }

    public get manualIps(): FormArray<FormControl<string>> {
        return this.manualForm.get('ipArr') as FormArray<FormControl<string>>;
    }

    public addManualIp(): void {
        const deviceType = (this.manualForm.get('deviceType')?.value || 'camera') as 'camera' | 'nvr';
        if (deviceType === 'nvr') {
            return;
        }
        this.manualIps.push(
            this.fb.control('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^(\d{1,3}\.){3}\d{1,3}$/)] })
        );
    }

    public removeManualIp(index: number): void {
        if (this.manualIps.length > 1) {
            this.manualIps.removeAt(index);
        }
    }

    private emitManualSelection(): void {
        if (!this.manualForm) return;

        const deviceType = (this.manualForm.get('deviceType')?.value || 'camera') as 'camera' | 'nvr';
        let ipArr = (this.manualIps.value as string[]).map(v => (v || '').trim()).filter(Boolean);
        if (deviceType === 'nvr') {
            ipArr = ipArr.slice(0, 1);
        }

        this.manualConfigChanged.emit({ deviceType, ipArr });

        // Convert IP list into placeholder cameras (id = ip) so wizard Next can work
        const cameras: Camera[] = ipArr.map(ip => ({
            id: ip,
            ipAddress: ip,
            name: ip,
            cameraBrand: 'Unknown' as any,
        }));
        this.camerasSelected.emit(cameras);
    }

    private startScan(): void {
        this.isScanning = true;
        this.scanProgress = 0;
        this.currentSessionToken = null; // Reset session token

        // Call appropriate API based on detection method
        if (this.detectionMethod === AddCameraWizardModels.DetectionMethod.OnvifSearch) {
            this.edgeService.discoverCameras(this.edgeId).subscribe({
                next: (response: any) => {
                    console.log('ONVIF scan started', response);
                    // Store the session token (messageId) from the response
                    if (response?.messageId) {
                        this.currentSessionToken = response.messageId;
                    } else if (response?.token?.session) {
                        this.currentSessionToken = response.token.session;
                    }
                },
                error: (error) => {
                    console.error('Failed to start ONVIF scan', error);
                    this.isScanning = false;
                }
            });
        } else {
            // Express or Address Range (both use same endpoint with different params)
            const scanData = this.addressRangeData || {
                ipStart: '',
                ipEnd: '',
                port: 554
            };

            this.edgeService.scanCameras(this.edgeId, scanData).subscribe({
                next: (response: any) => {
                    console.log('Scan started', response);
                    // Store the session token (messageId) from the response
                    if (response?.messageId) {
                        this.currentSessionToken = response.messageId;
                    } else if (response?.token?.session) {
                        this.currentSessionToken = response.token.session;
                    }
                },
                error: (error) => {
                    console.error('Failed to start scan', error);
                    this.isScanning = false;
                }
            });
        }
    }

    private subscribeToScanUpdates(): void {
        this.socketMainService
            .consume<any>(SocketEvents.cameraDiscoveryNotification)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                console.log('Camera discovery notification:', response);

                // Filter by sessionToken - only process notifications from current scan
                const notificationSessionToken = response?.sessionToken;
                if (this.currentSessionToken && notificationSessionToken !== this.currentSessionToken) {
                    console.log(`Ignoring scan notification from different session: ${notificationSessionToken} (current: ${this.currentSessionToken})`);
                    return;
                }

                const payload = response as unknown as { discoveredCameras: string };

                if (!payload?.discoveredCameras) {
                    return;
                }

                try {
                    const discoveredCameras: { progress: number, batch: any[] } = JSON.parse(payload.discoveredCameras);

                    // Update progress
                    if (discoveredCameras?.progress || discoveredCameras?.progress === 0) {
                        this.scanProgress = discoveredCameras.progress;

                        if (this.scanProgress >= 100) {
                            this.isScanning = false;
                            this.progressDone = true;
                        }
                    }

                    // Add cameras from batch
                    if (discoveredCameras?.batch && Array.isArray(discoveredCameras.batch)) {
                        const batch = discoveredCameras.batch.map((d: any) => {
                            return {
                                id: uuid.v4(),
                                name: d.name
                                    ? decodeURI(d.name)
                                    : (d.isCameraBrand
                                        ? this.translateService.instant('ADD_CAMERA_WIZARD.SCAN.DEFAULT_CAMERA_NAME', { index: this.cameraNum++ })
                                        : this.translateService.instant('ADD_CAMERA_WIZARD.SCAN.DEFAULT_DEVICE_NAME', { index: this.deviceNum++ })),
                                cameraId: d.macAddress,
                                ipAddress: d.ipAddress,
                                status: d.status,
                                mac: d.macAddress,
                                macAddress: d.macAddress,
                                cameraBrand: d.cameraBrand,
                                isCameraBrand: d.isCameraBrand,
                                pingStats: d.pingMs ? {
                                    min: d.pingMs.min,
                                    max: d.pingMs.max,
                                    avg: d.pingMs.avg,
                                    std: d.pingMs.mdev,
                                    state: d.pingMs.state,
                                } : null,
                                onvifData: {
                                    urn: d.urn,
                                    name: d.name,
                                    hardware: d.hardware,
                                    location: d.location,
                                    types: d.types,
                                    xaddrs: d.xaddrs,
                                    scopes: d.scopes,
                                },
                            } as Camera;
                        });

                        // Apply camera naming logic (like original implementation)
                        // for (let i = 0; i < batch.length; i++) {
                        //     if (batch[i].name === 'undefined' || !batch[i].name) {
                        //         batch[i].name = (!batch[i].isCameraBrand
                        //             ? this.translateService.instant('ADD_CAMERA_WIZARD.SCAN.DEFAULT_DEVICE_NAME', { index: deviceNum++ })
                        //             : this.translateService.instant('ADD_CAMERA_WIZARD.SCAN.DEFAULT_CAMERA_NAME', { index: cameraNum++ }));
                        //     }
                        // }

                        this.discoveredCameras = [
                            ...this.discoveredCameras,
                            ...batch
                        ];
                        this.applySearch();
                    }
                } catch (error) {
                    console.error('Failed to parse discoveredCameras:', error);
                }
            });
    }

    public onCameraToggled(event: string | string[] | null): void {
        if (event === null) {
            // Clear all selection
            this.selectedCameras.clear();
            this.emitSelection();
        } else if (Array.isArray(event)) {
            this.selectedCameras.clear();
            event.forEach(id => this.selectedCameras.add(id));
            this.emitSelection();
        }
    }

    public toggleCamera(camera: Camera): void {
        const id = camera.id;
        if (this.selectedCameras.has(id)) {
            this.selectedCameras.delete(id);
        } else {
            this.selectedCameras.add(id);
        }
        this.emitSelection();
    }

    private emitSelection(): void {
        const selected = this.discoveredCameras.filter(camera =>
            this.selectedCameras.has(camera.id)
        );
        this.camerasSelected.emit(selected);
    }

    public isCameraSelected(camera: Camera): boolean {
        const id = camera.id;
        return this.selectedCameras.has(id);
    }

    public onBack(): void {
        this.back.emit();
    }

    public onNext(): void {
        const selected = this.discoveredCameras.filter(camera =>
            this.selectedCameras.has(camera.id)
        );
        this.camerasSelected.emit(selected);
    }

    public get isNextDisabled(): boolean {
        return this.selectedCameras.size === 0;
    }

    private applySearch(): void {
        const term = this.searchControl.value.toLowerCase().trim();
        if (!term) {
            this.filteredCameras = this.discoveredCameras;
            return;
        }
        this.filteredCameras = this.discoveredCameras.filter((cam) => {
            const mac = (cam.mac ?? cam.macAddress ?? '').toLowerCase();
            return (
                (cam.name ?? '').toLowerCase().includes(term) ||
                (cam.ipAddress ?? '').toLowerCase().includes(term) ||
                mac.includes(term)
            );
        });
    }

    public isLegacy(metadata: Edge.V2.EdgePulsationMetadata | null | undefined): boolean {
        const version = metadata?.edgeStatus?.msgs_version;
        if (!version) {
            return false;
        }
        const versionNum = +version.split('.').splice(0, 3).join('');
        return versionNum <= 16301;
    }

    public manual() {
        const data: CameraActivateDialogData = {
            manual: true,
        };
        return this.dialog.open(CameraActivateDialogComponent, {
            width: '688px',
            minHeight: '640px',
            panelClass: 'modal-no-padding',
            data,
        });
    }

    public onCsvFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.processCsvFile(file);
        }
        input.value = '';
    }

    public onCsvFileDropped(files: FileList | File[]): void {
        const file = Array.from(files)[0];
        if (file) {
            this.processCsvFile(file);
        }
    }

    public downloadExampleCsv(): void {
        const link = document.createElement('a');
        link.href = 'assets/templates/camera-import-example.csv';
        link.download = 'camera-import-example.csv';
        link.click();
    }

    public clearCsvImport(): void {
        this.csvFileName = null;
        this.csvParseErrors = [];
        this.csvImportedRows = [];
        this.discoveredCameras = [];
        this.filteredCameras = [];
        this.camerasSelected.emit([]);
        this.csvImportCompleted.emit([]);
        this.manualConfigChanged.emit({ deviceType: 'camera', ipArr: [] });
    }

    private processCsvFile(file: File): void {
        if (!file.name.toLowerCase().endsWith('.csv')) {
            this.csvParseErrors = ['ADD_CAMERA_WIZARD.CSV.ERRORS.INVALID_FILE_TYPE'];
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const content = String(reader.result ?? '');
            const result = WizardCameraCsvUtil.parseCameraImportCsv(content);

            this.csvFileName = file.name;
            this.csvParseErrors = result.errors;

            if (!result.rows.length) {
                this.csvImportedRows = [];
                this.discoveredCameras = [];
                this.filteredCameras = [];
                this.camerasSelected.emit([]);
                this.csvImportCompleted.emit([]);
                this.manualConfigChanged.emit({ deviceType: 'camera', ipArr: [] });
                return;
            }

            this.csvImportedRows = result.rows;
            const ipArr = result.rows.map((row) => row.ipAddress);
            this.manualConfigChanged.emit({ deviceType: 'camera', ipArr });

            this.discoveredCameras = result.rows.map((row) => ({
                id: row.ipAddress,
                ipAddress: row.ipAddress,
                name: row.name,
                cameraBrand: 'Unknown' as any,
            } as Camera));
            this.filteredCameras = [...this.discoveredCameras];

            this.csvImportCompleted.emit(result.rows);
            this.camerasSelected.emit(this.discoveredCameras);
        };
        reader.onerror = () => {
            this.csvParseErrors = ['ADD_CAMERA_WIZARD.CSV.ERRORS.READ_FAILED'];
        };
        reader.readAsText(file);
    }

    public formatCsvError(errorKey: string): string {
        if (errorKey.startsWith('ADD_CAMERA_WIZARD.CSV.ERRORS.MISSING_COLUMNS:')) {
            const columns = errorKey.split(':')[1];
            return this.translateService.instant('ADD_CAMERA_WIZARD.CSV.ERRORS.MISSING_COLUMNS', { columns });
        }
        if (errorKey.includes(':')) {
            const [key, param] = errorKey.split(':');
            return this.translateService.instant(key, { row: param });
        }
        return this.translateService.instant(errorKey);
    }
}
